import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { DASHBOARD, UPCOMINGTASK, WORKALL } from "../../services/actions";
import { Grid, Box, Typography } from "@mui/material";
import { FaShoppingBag, FaUsers, FaProjectDiagram, FaCamera, FaTasks, FaLayerGroup, FaClipboardList } from "react-icons/fa";
import { MdOutlinePendingActions, MdOutlineTaskAlt } from "react-icons/md";
import { RiTimeLine, RiAlertLine } from "react-icons/ri";
import PressureMeter from './PressureMeter';
import StatusDot from './StatusDot';
import GrowthChart from './GrowthChart';
import './dashboard.css';

/* ── card accent config (icon + css class, indexed by card order) ── */
const CARD_ICONS = [
  <FaUsers />,
  <FaProjectDiagram />,
  <FaCamera />,
  <FaTasks />,
  <FaLayerGroup />,
  <FaClipboardList />,
];
const CARD_ICON_CLASSES = [
  'icon-indigo',
  'icon-blue',
  'icon-emerald',
  'icon-amber',
  'icon-pink',
  'icon-red',
];

function getStatusBadgeClass(status = '') {
  const s = status.toLowerCase().replace(' ', '-');
  if (s.includes('complet')) return 'task-status-badge badge-completed';
  if (s.includes('progress')) return 'task-status-badge badge-progress';
  if (s.includes('overdue')) return 'task-status-badge badge-overdue';
  return 'task-status-badge badge-pending';
}

export default function Dashboard() {
  
  const dispatch = useDispatch();
  const dashboardState = useSelector((state) => state?.dashboardState);
  const upcomingtaskState = useSelector((state) => state?.upcomingtaskState);
  const workallState = useSelector((state) => state?.workallState);
  const navigate = useNavigate();

  const user = React.useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("auth") || "{}");
    } catch (_) {
      return {};
    }
  }, []);

  const permissions = user?.permissions || [];
  const isSuperUser = Boolean(user?.is_superuser);

  const canViewWork = isSuperUser || permissions.includes("work.view_work");
  const canViewClient = isSuperUser || permissions.includes("client.view_client");
  const canViewService = isSuperUser || permissions.includes("services.view_service") || permissions.includes("service.view_service");
  const canViewTask = isSuperUser || permissions.includes("task.view_task") || permissions.includes("work.view_worktask");

  React.useEffect(() => {
    try {
      dispatch({ type: `${DASHBOARD}_GET`, payload: 0 });
      if (canViewTask) {
        dispatch({ type: `${UPCOMINGTASK}_GET`, payload: 0 });
      }
      if (canViewWork) {
        dispatch({ type: `${WORKALL}_GET`, payload: "work/0/" });
      }
    } catch (_) { }
  }, [canViewTask, canViewWork]);

  /* Build progress object for StatusDot */
  let progress = dashboardState?.data?.counts?.filter((d) =>
    ["Pending", "In Progress", "Completed", "Overdue"].includes(d.name)
  );
  progress = (progress || []).reduce((acc, item) => {
    acc[item.name] = item.count;
    return acc;
  }, {});

  const counts = (dashboardState?.data?.counts || []).filter((d) => {
    const lower = d.name?.toLowerCase() || '';
    if (lower.includes('client') && !canViewClient) return false;
    if ((lower.includes('service') || lower.includes('project')) && !canViewService) return false;
    if (lower.includes('work') && !canViewWork) return false;
    if (['pending', 'in progress', 'completed', 'overdue'].includes(lower) && !canViewWork && !canViewTask) return false;
    return true;
  });

  const tasks = upcomingtaskState?.data || [];
  const recentWorks = workallState?.data?.results || [];

  const handleCardClick = (name) => {
    const lower = name?.toLowerCase() || '';
    if (lower.includes('client') && canViewClient) navigate('/clients/');
    else if ((lower.includes('service') || lower.includes('project')) && canViewService) navigate('/services/');
    else if (lower.includes('work') && canViewWork) navigate('/works/');
    else if (lower.includes('task') && !lower.includes('completed') && !lower.includes('pending') && !lower.includes('progress') && !lower.includes('overdue') && canViewTask) navigate('/tasks/');
    else if (canViewWork) {
      let statusParam = lower.replace(/\s+/g, '_');
      if (lower === 'in progress') statusParam = 'in_progress';
      else if (lower === 'to do') statusParam = 'todo';
      else if (lower === 'under review') statusParam = 'undereview';
      navigate('/works/', { state: { status: statusParam } });
    }
  };

  return (
    <Box className="dash-wrapper">

      {/* ── Header ──────────────────────────────────────── */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography sx={{ fontSize: 22, fontWeight: 800, color: 'var(--text-primary)', lineHeight: 1.2 }}>
            Dashboard
          </Typography>
          <Typography sx={{ fontSize: 12, color: 'var(--text-muted)', mt: 0.4 }}>
            Overview of your workspace metrics
          </Typography>
        </Box>
        <Box sx={{
          background: 'rgba(99,102,241,0.07)',
          border: '1px solid rgba(99,102,241,0.18)',
          borderRadius: '12px',
          px: 2, py: 0.8,
          display: 'flex', alignItems: 'center', gap: 1
        }}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: '#10b981', boxShadow: '0 0 6px rgba(16,185,129,0.6)' }} />
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#6366f1' }}>Live</Typography>
        </Box>
      </Box>

      {/* ── Stat Cards ──────────────────────────────────── */}
      <div className="dash-section-title">Key Metrics</div>
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: 'repeat(2, 1fr)',
          sm: 'repeat(3, 1fr)',
          md: 'repeat(4, 1fr)',
          lg: `repeat(${Math.max(counts.length || 7, 7)}, 1fr)`
        },
        gap: 2,
        mb: 3.5,
        width: '100%'
      }}>
        {counts.map((data, i) => (
          <Box
            key={i}
            onClick={() => handleCardClick(data.name)}
            className={`stat-card stat-card-${i % 6}`}
            sx={{
              cursor: 'pointer',
              width: '100%',
              boxSizing: 'border-box',
              '&:hover': { transform: 'translateY(-2px)' }
            }}
          >
            {/* top colour stripe */}
            <span className="card-stripe" />

            {data?.grouped ? (
              /* ── Grouped card (e.g. Tasks breakdown) ── */
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box className={`stat-icon-badge ${CARD_ICON_CLASSES[i % 6]}`}>
                    {CARD_ICONS[i % 6]}
                  </Box>
                  <Typography className="stat-label" sx={{ color: 'var(--text-secondary)', fontWeight: 700, fontSize: 13 }}>
                    {data.name}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 0.5 }}>
                  {data.grouped.map((g, gi) => (
                    <Box key={gi} className="stat-group-row">
                      <span className="stat-group-label">{g.name}</span>
                      <span className="stat-group-count">{g.count}</span>
                    </Box>
                  ))}
                </Box>
              </>
            ) : (
              /* ── Simple count card ── */
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box className={`stat-icon-badge ${CARD_ICON_CLASSES[i % 6]}`}>
                    {CARD_ICONS[i % 6]}
                  </Box>
                </Box>
                <Box>
                  <div className="stat-count">{data.count}</div>
                  <div className="stat-label">{data.name}</div>
                </Box>
              </>
            )}
          </Box>
        ))}
      </Box>

      {/* ── Growth Trends (Chart.js) ────────────────────── */}
      {(canViewWork || canViewClient) && (
        <>
          <div className="dash-section-title">Workspace Growth Trends</div>
          <Grid container spacing={2.5} sx={{ mb: 3.5, width: '100%' }}>
            {canViewWork && (
              <Grid size={{ xs: 12, md: canViewClient ? 6 : 12, lg: canViewClient ? 6 : 12 }}>
                <Box className="panel-card" sx={{ height: '100%', minHeight: 320, boxSizing: 'border-box' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{
                      width: 32, height: 32, borderRadius: '10px',
                      background: 'rgba(99,102,241,0.1)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: '#6366f1', fontSize: 15
                    }}>
                      <FaProjectDiagram />
                    </Box>
                    <span className="panel-card-title">Recent Works</span>
                  </Box>
                  <div className="panel-divider" />
                  <Box className="task-scroll" sx={{ maxHeight: 250, overflowY: 'auto' }}>
                    {recentWorks.length === 0 && (
                      <Box sx={{ textAlign: 'center', py: 4 }}>
                        <FaProjectDiagram style={{ fontSize: 36, color: 'var(--text-muted)' }} />
                        <Typography sx={{ fontSize: 12, color: 'var(--text-muted)', mt: 1 }}>No recent works</Typography>
                      </Box>
                    )}
                    {recentWorks.slice(0, 5).map((work, i) => {
                      const formattedDate = new Date(work.created_at || work.client_creation_date).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                      });
                      const isOverdue = work.is_overdue && !['completed', 'approved'].includes(work.status);
                      const displayStatus = isOverdue ? 'Overdue' : (work.status || 'Pending');
                      return (
                        <Box key={i} className="task-item" onClick={() => navigate('/works/', { state: { filter: { id: work.id } } })} sx={{ cursor: 'pointer', '&:hover': { background: 'rgba(99,102,241,0.04)' } }}>
                          <div className="task-date-badge">{formattedDate !== "Invalid Date" ? formattedDate : "N/A"}</div>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <div className="task-name" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {work.work_name}
                            </div>
                            <div className="task-due" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {work.client_name || work.company_name}
                            </div>
                          </Box>
                          <span className={getStatusBadgeClass(displayStatus)}>
                            {displayStatus.replace('_', ' ')}
                          </span>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              </Grid>
            )}
            {canViewClient && (
              <Grid size={{ xs: 12, md: canViewWork ? 6 : 12, lg: canViewWork ? 6 : 12 }}>
                <GrowthChart modelName="client" title="Client Onboarding" type="line" color="#10b981" />
              </Grid>
            )}
          </Grid>
        </>
      )}

      {/* ── Lower panels ────────────────────────────────── */}
      {(canViewTask || canViewWork) && (
        <>
          <div className="dash-section-title">Analytics &amp; Activity</div>
          <Grid container spacing={2.5} sx={{ width: '100%' }}>

            {/* Upcoming Tasks */}
            {canViewTask && (
              <Grid size={{ xs: 12, sm: 12, md: canViewWork ? 7.5 : 12, lg: canViewWork ? 7.5 : 12 }}>
                <Box className="panel-card" sx={{ height: '100%', boxSizing: 'border-box' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{
                      width: 32, height: 32, borderRadius: '10px',
                      background: 'rgba(99,102,241,0.1)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: '#6366f1', fontSize: 15
                    }}>
                      <RiTimeLine />
                    </Box>
                    <span className="panel-card-title">Upcoming Task Deadlines</span>
                  </Box>

                  <div className="panel-divider" />

                  <Box className="task-scroll">
                    {tasks.length === 0 && (
                      <Box sx={{ textAlign: 'center', py: 4 }}>
                        <MdOutlineTaskAlt style={{ fontSize: 36, color: 'var(--text-muted)' }} />
                        <Typography sx={{ fontSize: 12, color: 'var(--text-muted)', mt: 1 }}>No upcoming tasks</Typography>
                      </Box>
                    )}
                    {tasks.map((data, i) => {
                      const formatted = new Date(data.assignment__due_date).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                      });
                      return (
                        <Box key={i} className="task-item" onClick={() => navigate('/works/', { state: { filter: { id: data.work__id || data.work } } })} sx={{ cursor: 'pointer', '&:hover': { background: 'rgba(99,102,241,0.04)' } }}>
                          <div className="task-date-badge">{formatted}</div>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <div className="task-name" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {data.task__task_name}
                            </div>
                            <div className="task-due">{data?.due_date?.split("T")[0]}</div>
                          </Box>
                          <span className={getStatusBadgeClass(data?.computed_status)}>
                            {data?.computed_status}
                          </span>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              </Grid>
            )}

            {/* Status Donut */}
            {canViewWork && (
              <Grid size={{ xs: 12, sm: 12, md: canViewTask ? 4.5 : 12, lg: canViewTask ? 4.5 : 12 }}>
                <Box className="panel-card" sx={{ height: '100%', boxSizing: 'border-box' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{
                      width: 32, height: 32, borderRadius: '10px',
                      background: 'rgba(16,185,129,0.1)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: '#10b981', fontSize: 15
                    }}>
                      <MdOutlineTaskAlt />
                    </Box>
                    <span className="panel-card-title">Work Status Overview</span>
                  </Box>

                  <div className="panel-divider" />

                  <Box className="chart-center" sx={{ py: 1 }}>
                    {/* Override StatusDot's internal font colours for dark theme */}
                    <StatusDot data={progress} size={130} onStatusClick={(status) => navigate('/works/', { state: { status: status.toLowerCase().replace(' ', '_') } })} />
                  </Box>
                </Box>
              </Grid>
            )}
          </Grid>
        </>
      )}
    </Box>
  );
}
